// frontend/src/pages/admin/AdminCreators.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Star, RefreshCw } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_BADGE = {
  active: 'bg-emerald-50 text-emerald-700',
  suspended: 'bg-amber-50 text-amber-700',
  banned: 'bg-red-50 text-red-600',
  archived: 'bg-gray-100 text-gray-500',
};
const VERIF_BADGE = {
  none: 'bg-gray-100 text-gray-500',
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-blue-50 text-blue-700',
  rejected: 'bg-red-50 text-red-600',
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value ?? '—'}</p>
  </div>
);

const AdminCreators = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [accountFilter, setAccountFilter] = useState('');
  const [verifFilter, setVerifFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    const h = { Authorization: `Bearer ${token}` };
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (accountFilter) params.set('account_status', accountFilter);
      if (verifFilter) params.set('verification_status', verifFilter);
      if (search) params.set('search', search);
      const [s, l] = await Promise.all([
        fetch(`${API_URL}/api/admin/marketplace/creators/stats`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/admin/marketplace/creators?${params}`, { headers: h }).then(r => r.ok ? r.json() : { items: [], total: 0 }),
      ]);
      setStats(s);
      setItems(l.items || []);
      setTotal(l.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, accountFilter, verifFilter, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-violet-600" /> Creators
        </h1>
        <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total} />
        <StatCard label="Active" value={stats?.active} />
        <StatCard label="Published" value={stats?.published} />
        <StatCard label="Verified" value={stats?.verified} />
        <StatCard label="Pending" value={stats?.pending_verification} />
        <StatCard label="Suspended" value={stats?.suspended} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name or email…"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
        <select value={accountFilter} onChange={e => { setAccountFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All accounts</option>
          {['active', 'suspended', 'banned', 'archived'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={verifFilter} onChange={e => { setVerifFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All verification</option>
          {['none', 'pending', 'verified', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-sm text-gray-500">No creators found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Creator</th>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-right px-4 py-3">Templates</th>
                  <th className="text-right px-4 py-3">Sales</th>
                  <th className="text-right px-4 py-3">Followers</th>
                  <th className="text-right px-4 py-3">Risk</th>
                  <th className="text-center px-4 py-3">Verification</th>
                  <th className="text-center px-4 py-3">Account</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/marketplace/creators/${c.id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {c.featured_creator && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                        <div>
                          <p className="font-medium text-gray-900">{c.display_name}</p>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{c.country || '—'}</td>
                    <td className="px-4 py-3 text-right">{c.metrics?.templates_count ?? 0}</td>
                    <td className="px-4 py-3 text-right">{c.metrics?.sales_count ?? 0}</td>
                    <td className="px-4 py-3 text-right">{c.metrics?.followers_count ?? 0}</td>
                    <td className="px-4 py-3 text-right font-semibold">{Math.round(c.creator_risk_score)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${VERIF_BADGE[c.verification_status] || ''}`}>
                        {c.verification_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ACCOUNT_BADGE[c.account_status] || ''}`}>
                        {c.account_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > LIMIT && (
          <div className="px-4 py-3 border-t border-gray-50 flex justify-end gap-2">
            {page > 1 && <button onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">Previous</button>}
            {items.length === LIMIT && <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700">Next</button>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCreators;
